/* theme.js - the socle, and the only script the head loads on every route.
   Environment flags, cart event bus, focus trap, fetch config, and the intent
   loader that keeps the overlay modules off the critical path until a pointer
   or a focus says they are wanted. Component behaviour never lands here: a
   module is assets/<name>.js, emitted as a deferred script by the section that
   renders it, so a route only pays for what it renders. */
(function () {
  'use strict';

  var root = document.documentElement;
  var loaded = Object.create(null);
  var subscribers = Object.create(null);

  /* environment */
  var supportsHover = window.matchMedia('(hover: hover)').matches;
  root.classList.add(supportsHover ? 'no-touch' : 'touch');

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Scrollbar width feeds the page-margin formula in design-tokens.liquid, which
     centres the capped column on the VIEWPORT rather than the layout box. Without
     it the column drifts by half a scrollbar on desktop Chrome. */
  function measureScrollbar() {
    var w = window.innerWidth - root.clientWidth;
    root.style.setProperty('--pp-scrollbar-width', (w > 0 ? w : 0) + 'px');
  }
  measureScrollbar();
  window.addEventListener('resize', debounce(measureScrollbar, 150));

  /* event bus */
  /* Cart mutations are published here, never read back off the DOM. Apps also
     listen on the document, so every publish re-dispatches as a DOM event. */
  var EVENTS = {
    cartUpdate: 'pp:cart:update',
    cartError: 'pp:cart:error',
    variantChange: 'pp:variant:change'
  };

  function subscribe(name, handler) {
    (subscribers[name] || (subscribers[name] = [])).push(handler);
    return function () {
      subscribers[name] = subscribers[name].filter(function (h) {
        return h !== handler;
      });
    };
  }

  /* HOW MANY TIMES THE CART HAS CHANGED IN THIS PAGE VIEW, counted here rather
     than in any one module because it has to be true for a listener that did
     not exist yet. The overlay modules are deliberately fetched on intent, and
     the cart drawer is routinely fetched BY the add that just changed the cart:
     it therefore subscribes one turn too late to hear the very update it was
     loaded for, and a module that only learns about mutations by subscribing
     starts life silently out of date. A counter that lives above the bus lets a
     late arrival compare what it rendered against what has happened. */
  var cartVersion = 0;

  function publish(name, detail) {
    if (name === EVENTS.cartUpdate) cartVersion += 1;
    (subscribers[name] || []).forEach(function (h) {
      try {
        h(detail);
      } catch (e) {
        console.error(name, e);
      }
    });
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  /* module loading */
  function loadModule(src) {
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return loaded[src];
  }

  /* Arm a trigger so its module downloads on the first sign of intent. `click`
     is the floor, not the trigger: a keyboard or touch user reaches the control
     without ever firing pointerover, and must still get the module. */
  function onIntent(trigger, src, run) {
    if (!trigger) return;
    var done = false;
    function fire(event) {
      if (done) return;
      done = true;
      ['pointerover', 'focusin', 'touchstart', 'click'].forEach(function (t) {
        trigger.removeEventListener(t, fire);
      });
      loadModule(src).then(function () {
        if (typeof run === 'function') run(event);
      });
    }
    ['pointerover', 'focusin', 'touchstart'].forEach(function (t) {
      trigger.addEventListener(t, fire, { once: true, passive: true });
    });
    trigger.addEventListener('click', fire);
  }

  /* Declarative intent, for triggers that cannot arm themselves. onIntent above
     wants a NODE, which suits a header control that exists once and outlives the
     page. It does not suit product cards: there are twenty of them, they are
     rendered by four different sections, and the facets module replaces the
     whole grid on every filter, so every binding would have to be redone and
     nobody owns the moment to redo it.

     So the module is declared in MARKUP instead. Any element carrying BOTH
     data-intent-src and data-intent-trigger says: download this module the first
     time a pointer, a focus or a tap lands inside something matching this
     selector. One delegated listener set per declaration, on the document, so a
     grid of two hundred cards costs the same as one. theme.js learns nothing
     about the component: the url and the selector both come from the DOM.

     THE FIRST PRESS IS NOT LOST. A module fetched on the click that wanted it
     arrives after the browser has already navigated or submitted. So that click
     is held back, and the SAME click is dispatched again on the same element
     once the module is there to answer it. If the module never arrives the
     replay runs unopposed and the markup's own floor happens instead: a link
     navigates, a product form posts to /cart/add. A modified click is left
     alone, because opening a card in a new tab must keep working. */
  /* THE THIRD ARM OF THE SAME CONTRACT: LOAD NOW, BECAUSE STORED STATE SAYS
     THERE IS ALREADY SOMETHING ON SCREEN TO RESTORE.

     `hint` and `press` below both wait for the shopper. Some overlays cannot:
     one that redraws a selection the shopper made on an earlier page view has to
     be on screen BEFORE they touch anything, or the page lies about what is held.
     That decision used to be written as an inline script in the section that
     needed it, and that is what makes this worth generalising rather than an
     over-fit: measured on the served theme, sections/product-compare.liquid's
     bootstrap was 3551 bytes of markup on EVERY page view, uncached, of which
     2398 were comment prose. Here the same words live in a file the browser
     caches once.

     THE CONDITION IS DECLARED IN MARKUP AND PARSED GENERICALLY, so this function
     still learns nothing about the component it is loading, exactly like the src
     and the selector above. Comma separated terms, any one of which is enough:

       key         load if the key holds anything at all
       key=value   load if it holds exactly that value
       key!=value  load if it holds something, and that something is not value

     The third form is the one that matters in practice: a list that has been
     emptied is stored as its empty JSON form rather than removed, so "present"
     and "non trivial" are different questions and only the caller knows the
     answer for its own key.

     WHAT THIS GIVES UP, DELIBERATELY. The old inline script could still restore a
     selection when assets/theme.js itself had failed to load, because it carried
     its own script-element fallback. This cannot: it IS theme.js. The trade is
     taken with eyes open, because a page view where theme.js is missing has no
     cart drawer, no search, no header behaviour and no way to open the compare
     dialog either, so restoring the bar alone would draw a control whose only
     button leads nowhere. Timing is NOT part of the trade: the inline script read
     storage mid parse but could only ever call loadModule at DOMContentLoaded,
     since window.PP does not exist while the body is still parsing, and armIntents
     below runs at exactly that moment. */
  function eagerReason(host) {
    if (host.hasAttribute('data-intent-eager-in-editor') && window.Shopify && window.Shopify.designMode) {
      return 'editor';
    }
    var terms = host.getAttribute('data-intent-eager-storage');
    if (!terms) return null;
    var store;
    /* Safari private mode throws on the READ as well as on the write, so the
       whole lookup is wrapped rather than each getItem. A shopper whose storage
       is unavailable has nothing stored to restore, so there is nothing to load. */
    try {
      store = window.localStorage;
    } catch (e) {
      return null;
    }
    var list = terms.split(',');
    for (var i = 0; i < list.length; i += 1) {
      var term = list[i].trim();
      if (!term) continue;
      var negated = term.indexOf('!=') > -1;
      var cut = negated ? term.indexOf('!=') : term.indexOf('=');
      var key = cut > -1 ? term.slice(0, cut) : term;
      var want = cut > -1 ? term.slice(cut + (negated ? 2 : 1)) : null;
      var held;
      try {
        held = store.getItem(key.trim());
      } catch (e) {
        return null;
      }
      if (held === null || held === '') continue;
      if (want === null) return term;
      if (negated ? held !== want : held === want) return term;
    }
    return null;
  }

  function armIntent(host) {
    if (host.hasAttribute('data-intent-armed')) return;
    var src = host.getAttribute('data-intent-src');
    var selector = host.getAttribute('data-intent-trigger');
    if (!src || !selector) return;
    host.setAttribute('data-intent-armed', '');

    /* Checked before the listeners are installed, not instead of them: a host
       that loads eagerly still wants them, because loadModule dedupes on src and
       the delegated press handler is what holds the FIRST click back until the
       module can answer it. Skipping them here would trade a byte for a lost tap. */
    if (eagerReason(host)) loadModule(src);

    var HINTS = ['pointerover', 'focusin', 'touchstart'];
    var state = 'idle';

    function match(event) {
      if (!event.target || !event.target.closest) return null;
      try {
        return event.target.closest(selector);
      } catch (e) {
        return null;
      }
    }

    /* The editor replaces a section's markup without an unload event, so the
       declaring node can leave the document while these listeners live on. */
    function orphaned() {
      if (host.isConnected) return false;
      dropHints();
      document.removeEventListener('click', press);
      return true;
    }

    function dropHints() {
      HINTS.forEach(function (type) {
        document.removeEventListener(type, hint);
      });
    }

    function hint(event) {
      if (orphaned() || !match(event)) return;
      dropHints();
      loadModule(src);
    }

    function press(event) {
      if (orphaned() || state === 'settled') return;
      if (event.defaultPrevented || event.button > 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var trigger = match(event);
      if (!trigger) return;
      event.preventDefault();
      if (state === 'pending') return;
      state = 'pending';
      var timer = null;
      function settle() {
        if (state === 'settled') return;
        state = 'settled';
        clearTimeout(timer);
        dropHints();
        document.removeEventListener('click', press);
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }
      /* A request that FAILS settles the promise, so the replay runs and the
         floor happens. A request that HANGS settles nothing, and the press would
         then be swallowed for the life of the page view, suppressing the very
         floors the markup carries. The wait is therefore bounded: past it the
         click is replayed unopposed, exactly as if the module had 404ed. */
      timer = setTimeout(settle, 3000);
      loadModule(src).then(settle, settle);
    }

    HINTS.forEach(function (type) {
      document.addEventListener(type, hint, { passive: true });
    });
    document.addEventListener('click', press);
  }

  function armIntents() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-intent-src][data-intent-trigger]'), armIntent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', armIntents);
  } else {
    armIntents();
  }
  document.addEventListener('shopify:section:load', armIntents);

  /* focus trap */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  var trap = null;

  /* IS THIS CONTROL SOMETHING THE UA WILL ACTUALLY FOCUS, ASKED OF THE LIVE TREE.
     checkVisibility is the only test that answers correctly for a collapsed
     <details>, and that is not a detail here: the filter panel is built out of
     native disclosure groups. MEASURED in Chromium 145 on a checkbox inside a
     closed group: offsetParent reports BODY, getClientRects returns one rect and
     offsetHeight is 21, so every classic "is it visible" heuristic calls it
     visible, while the UA refuses to focus it. The collapsed content keeps its
     boxes and is skipped through content-visibility on ::details-content instead
     of being display:none the way it used to be. visibilityProperty is asked for
     explicitly because the default ignores visibility: hidden, which is not
     focusable either.
     The fallback is for engines with no checkVisibility (Safari before 17.4,
     Firefox before 125). Those predate ::details-content and hide collapsed
     content by generating no boxes at all, which is exactly what offsetParent
     sees, so the old test is right on precisely the engines that need it. */
  function focusableNow(el) {
    if (typeof el.checkVisibility === 'function') return el.checkVisibility({ visibilityProperty: true });
    return el.offsetParent !== null;
  }

  /* The first or the last control the shopper can really reach, scanned from
     whichever end is asked for and stopping at the first hit, so the usual cost
     of a Tab is one test and not one per control in the surface. */
  function focusEdge(container, fromEnd) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < nodes.length; i += 1) {
      var el = nodes[fromEnd ? nodes.length - 1 - i : i];
      if (focusableNow(el)) return el;
    }
    return null;
  }

  /* THE BOUNDARY IS RESOLVED AT EVERY Tab, NEVER SNAPSHOTTED AT ARM TIME, and
     that is the whole difference between this and the version it replaces.

     Every other overlay in this theme is a native <dialog> opened with
     showModal(), and the UA recomputes its focus cycle from the live tree at
     every Tab. One surface cannot be a dialog: the filter panel is the same
     <form> that is a static sidebar from 1024px and a plain block above the grid
     with scripting off, and a dialog is none of those. It is therefore the only
     caller whose contents move while the trap is armed, and they move in two
     ways that are both nominal use:

       1. THE GROUPS ARE NATIVE <details>. Opening one reveals its checkboxes,
          with no script anywhere in the path, so a list frozen at arm time can
          never contain them. Tab off the last one walked out of the panel and
          landed on the page behind the scrim, focus invisible under a 40% dim.
       2. assets/facets.js RECONCILES the form after a filter change rather than
          replacing it, and the one thing it writes on each checkbox is
          `disabled`. FOCUSABLE excludes disabled controls, so a frozen first or
          last that the server has just disabled can never equal
          document.activeElement again and its wrap stops firing for good.

     And the mirror image, which is worse than a leak because it is a real trap:
     when the frozen last node is merely HIDDEN, the shopper collapsing the group
     it lived in, the wrap does fire and calls focus() on an unfocusable element,
     which does nothing at all. Shift+Tab then moves the shopper nowhere,
     silently, for the rest of the session.

     Resolving the two edges inside the handler costs one querySelectorAll per Tab
     press, which is not a hot loop, and it buys back the property the snapshot
     quietly gave up: the trap describes the surface as it is now.
     Bench: state/tools/verify-focus-trap.mjs. */
  function trapFocus(container, firstFocus) {
    removeTrapFocus();
    var opening = firstFocus || focusEdge(container, false);
    if (!opening) return;

    function onKey(event) {
      if (event.key !== 'Tab') return;
      var first = focusEdge(container, false);
      var last = focusEdge(container, true);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    trap = { container: container, onKey: onKey, returnTo: document.activeElement };
    container.addEventListener('keydown', onKey);
    opening.focus();
  }

  function removeTrapFocus(restore) {
    if (!trap) return;
    trap.container.removeEventListener('keydown', trap.onKey);
    var back = restore || trap.returnTo;
    trap = null;
    if (back && typeof back.focus === 'function') back.focus();
  }

  /* details */
  /* Accordions are native <details>. The only behaviour the platform does not
     give us is Escape, which every disclosure is expected to honour. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.activeElement && document.activeElement.closest('details[open]');
    if (!open) return;
    open.removeAttribute('open');
    var summary = open.querySelector('summary');
    if (summary) summary.focus();
  });

  /* input modality, for the two controls whose focus lives in a shadow root
     (2026-09-24). `:focus-visible` cannot see inside <shopify-account> or the
     wallet button, so base.css and snippets/account-menu.liquid used to draw
     their ring on `:focus-within`, which matches a TAP too: the ring the founder
     saw on the account button. This attribute says "the last thing the shopper
     used was the keyboard", and those two rules now ask for it. Every other
     control keeps the platform's own :focus-visible. */
  (function () {
    var root = document.documentElement;
    document.addEventListener(
      'keydown',
      function (event) {
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        root.setAttribute('data-pp-kbd', '');
      },
      true
    );
    document.addEventListener(
      'pointerdown',
      function () {
        root.removeAttribute('data-pp-kbd');
      },
      true
    );
  })();

  /* helpers */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function fetchConfig(type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + (type || 'json')
      }
    };
  }

  window.PP = {
    EVENTS: EVENTS,
    publish: publish,
    subscribe: subscribe,
    /* A function, not a number: a property read once at module load would
       snapshot the value and never move again. */
    cartVersion: function () {
      return cartVersion;
    },
    loadModule: loadModule,
    onIntent: onIntent,
    trapFocus: trapFocus,
    removeTrapFocus: removeTrapFocus,
    debounce: debounce,
    fetchConfig: fetchConfig,
    reducedMotion: reducedMotion
  };
})();
